import { FC, useState, useEffect } from "react";
import type { Application, Course, PaymentStatus, Analytics } from "../types";
import {
  fetchApplications,
  fetchCourses,
  fetchAnalytics,
  updateApplicationStatus,
} from "../services/api";
import { formatPrice } from "../utils/validation";
import { useBranding } from "../context/BrandingContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Admin Dashboard - Manage applications and view analytics
 * Light theme, table-based, simple UX
 * Protected route - requires authentication
 */
const AdminDashboard: FC = () => {
  const brand = useBranding();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [applications, setApplications] = useState<Application[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterCourse, setFilterCourse] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<PaymentStatus | "">("");
  const [filterBatch, setFilterBatch] = useState<string>("");

  // Modal state
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [updating, setUpdating] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, [filterCourse, filterStatus, filterBatch]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters: { courseId?: string; paymentStatus?: PaymentStatus; batch?: string } = {};
      if (filterCourse) filters.courseId = filterCourse;
      if (filterStatus) filters.paymentStatus = filterStatus;
      if (filterBatch) filters.batch = filterBatch;

      const [appsData, coursesData, analyticsData] = await Promise.all([
        fetchApplications(filters),
        fetchCourses(),
        fetchAnalytics(),
      ]);

      setApplications(appsData);
      setCourses(coursesData);
      setAnalytics(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Update application status
  const handleStatusUpdate = async (id: string, status: PaymentStatus, batch?: string) => {
    try {
      setUpdating(true);
      await updateApplicationStatus(id, { paymentStatus: status, batch });
      await loadData();
      setSelectedApp(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  // Get unique batches from applications
  const uniqueBatches = [...new Set(applications.map((a) => a.batch).filter(Boolean))] as string[];

  // Status badge colors
  const statusColors: Record<PaymentStatus, string> = {
    PENDING: "bg-yellow-100 text-yellow-800",
    PAYMENT_SUBMITTED: "bg-blue-100 text-blue-800",
    CONFIRMED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
  };

  const statusLabels: Record<PaymentStatus, string> = {
    PENDING: "Pending",
    PAYMENT_SUBMITTED: "Payment Submitted",
    CONFIRMED: "Confirmed",
    REJECTED: "Rejected",
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={brand.logo.favicon} alt="" className="h-10 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-neutral-900">Admin Dashboard</h1>
                <p className="text-sm text-neutral-500">
                  Welcome, <span className="font-medium">{admin?.username}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="text-sm font-medium text-neutral-600 hover:text-brand-secondary transition-colors"
              >
                ← Application Form
              </a>
              <button
                onClick={() => {
                  logout();
                  navigate("/admin/login");
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Analytics Cards */}
        {analytics && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Overview</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Total Applications */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500">Total Applications</p>
                <p className="mt-2 text-3xl font-bold text-neutral-900">{analytics.total}</p>
              </div>

              {/* Confirmed */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500">Confirmed Payments</p>
                <p className="mt-2 text-3xl font-bold text-green-600">
                  {analytics.byStatus.find((s) => s.status === "CONFIRMED")?.count || 0}
                </p>
              </div>

              {/* Pending Review */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500">Awaiting Review</p>
                <p className="mt-2 text-3xl font-bold text-blue-600">
                  {analytics.byStatus.find((s) => s.status === "PAYMENT_SUBMITTED")?.count || 0}
                </p>
              </div>

              {/* Pending Payment */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500">Pending Payment</p>
                <p className="mt-2 text-3xl font-bold text-yellow-600">
                  {analytics.byStatus.find((s) => s.status === "PENDING")?.count || 0}
                </p>
              </div>
            </div>

            {/* Course & Referral Stats */}
            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              {/* By Course */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500 mb-3">By Course</p>
                <div className="space-y-2">
                  {analytics.byCourse.map((c) => (
                    <div key={c.courseId} className="flex justify-between items-center">
                      <span className="text-sm text-neutral-700">{c.courseName}</span>
                      <span className="font-semibold text-neutral-900">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Referral Source */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
                <p className="text-sm font-medium text-neutral-500 mb-3">By Referral Source</p>
                <div className="space-y-2">
                  {analytics.byReferral.slice(0, 5).map((r, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-sm text-neutral-700">{r.source || "Not specified"}</span>
                      <span className="font-semibold text-neutral-900">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Filters */}
        <section className="mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-neutral-200">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Course</label>
                <select
                  value={filterCourse}
                  onChange={(e) => setFilterCourse(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
                >
                  <option value="">All Courses</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as PaymentStatus | "")}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="PAYMENT_SUBMITTED">Payment Submitted</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Batch</label>
                <select
                  value={filterBatch}
                  onChange={(e) => setFilterBatch(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
                >
                  <option value="">All Batches</option>
                  {uniqueBatches.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilterCourse("");
                    setFilterStatus("");
                    setFilterBatch("");
                  }}
                  className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Table */}
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">
              Applications ({applications.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div
                className="h-8 w-8 animate-spin rounded-full border-4 border-t-transparent mx-auto"
                style={{ borderColor: `${brand.colors.primary}40`, borderTopColor: brand.colors.primary }}
              />
            </div>
          ) : error ? (
            <div className="p-12 text-center text-red-600">{error}</div>
          ) : applications.length === 0 ? (
            <div className="p-12 text-center text-neutral-500">No applications found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {applications.map((app) => (
                    <tr key={app.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-neutral-900">
                            {[app.firstName, app.lastName].filter(Boolean).join(" ") || "—"}
                          </p>
                          <p className="text-sm text-neutral-500">{app.email || app.mobile || "—"}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-neutral-900">{app.course.name}</p>
                        {app.batch && (
                          <p className="text-xs text-neutral-500">Batch: {app.batch}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold" style={{ color: brand.colors.primary }}>
                          {formatPrice(app.course.price)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[app.paymentStatus]}`}>
                          {statusLabels[app.paymentStatus]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-500">
                        {new Date(app.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setSelectedApp(app)}
                          className="text-sm font-medium text-brand-primary hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Application Detail Modal */}
      {selectedApp && (
        <ApplicationModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onUpdateStatus={handleStatusUpdate}
          updating={updating}
        />
      )}
    </div>
  );
};

// Application Detail Modal Component
interface ApplicationModalProps {
  application: Application;
  onClose: () => void;
  onUpdateStatus: (id: string, status: PaymentStatus, batch?: string) => Promise<void>;
  updating: boolean;
}

const ApplicationModal: FC<ApplicationModalProps> = ({
  application,
  onClose,
  onUpdateStatus,
  updating,
}) => {
  const brand = useBranding();
  const [newStatus, setNewStatus] = useState<PaymentStatus>(application.paymentStatus);
  const [newBatch, setNewBatch] = useState(application.batch || "");

  const statusColors: Record<PaymentStatus, string> = {
    PENDING: "bg-yellow-100 text-yellow-800",
    PAYMENT_SUBMITTED: "bg-blue-100 text-blue-800",
    CONFIRMED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">Application Details</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[application.paymentStatus]}`}>
              {application.paymentStatus.replace("_", " ")}
            </span>
            <span className="text-sm text-neutral-500">
              ID: {application.id.slice(0, 8)}...
            </span>
          </div>

          {/* Personal Info */}
          <div>
            <h4 className="text-sm font-medium text-neutral-500 mb-2">Personal Information</h4>
            <div className="bg-neutral-50 rounded-lg p-4 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-neutral-500">Name</p>
                <p className="font-medium">{[application.firstName, application.lastName].filter(Boolean).join(" ") || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Mobile</p>
                <p className="font-medium">{application.mobile || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Email</p>
                <p className="font-medium">{application.email || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Emergency Contact</p>
                <p className="font-medium">{application.emergencyContact || "—"}</p>
              </div>
            </div>
          </div>

          {/* Course Info */}
          <div>
            <h4 className="text-sm font-medium text-neutral-500 mb-2">Course Details</h4>
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-neutral-900">{application.course.name}</p>
                  <p className="text-sm text-neutral-500">{application.course.type}</p>
                </div>
                <p className="text-xl font-bold" style={{ color: brand.colors.primary }}>
                  {formatPrice(application.course.price)}
                </p>
              </div>
            </div>
          </div>

          {/* Professional Info */}
          {(application.profession || application.organizationName || application.institutionName) && (
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-2">Professional / Academic</h4>
              <div className="bg-neutral-50 rounded-lg p-4 grid gap-3 sm:grid-cols-2">
                {application.profession && (
                  <div>
                    <p className="text-xs text-neutral-500">Profession</p>
                    <p className="font-medium">{application.profession.replace("_", " ")}</p>
                  </div>
                )}
                {application.organizationName && (
                  <div>
                    <p className="text-xs text-neutral-500">Organization</p>
                    <p className="font-medium">{application.organizationName}</p>
                  </div>
                )}
                {application.institutionName && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-neutral-500">Institution</p>
                    <p className="font-medium">{application.institutionName}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Info */}
          {(application.motivation || application.referralSource) && (
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-2">Additional Information</h4>
              <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                {application.motivation && (
                  <div>
                    <p className="text-xs text-neutral-500">Motivation</p>
                    <p className="text-sm">{application.motivation}</p>
                  </div>
                )}
                {application.referralSource && (
                  <div>
                    <p className="text-xs text-neutral-500">Referral Source</p>
                    <p className="font-medium">{application.referralSource}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Actions */}
          <div className="border-t border-neutral-200 pt-6">
            <h4 className="text-sm font-medium text-neutral-500 mb-3">Update Application</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Payment Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as PaymentStatus)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
                >
                  <option value="PENDING">Pending</option>
                  <option value="PAYMENT_SUBMITTED">Payment Submitted</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Assign Batch
                </label>
                <input
                  type="text"
                  value={newBatch}
                  onChange={(e) => setNewBatch(e.target.value)}
                  placeholder="e.g., Jan 2026 Batch"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                onClick={() => onUpdateStatus(application.id, newStatus, newBatch || undefined)}
                disabled={updating}
                className="flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition-all disabled:opacity-50"
                style={{ backgroundColor: brand.colors.primary }}
              >
                {updating ? "Updating..." : "Save Changes"}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg border border-neutral-300 text-neutral-700 font-medium hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
